import {
  MemoizedSelector,
  createSelector,
  createSelectorFactory,
  defaultMemoize,
} from '@ngrx/store';

import { OfferBrief } from '@recruitee/http/types';
import { LocationSelectors } from '@recruitee/locations/data';
import { createEntityCollectionStore, memoizeSelector } from '@recruitee/ngrx';

import { CanAssignToJobsWithStatuses, EntityStoreKey, OfferScope } from '../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<OfferBrief>()(
  EntityStoreKey.JobsBrief,
  {
    references: makeRef => [
      makeRef('locationIds', 'locations', LocationSelectors.byIds),
      makeRef(
        'locationIds',
        'locationsText',
        locationIds => state =>
          LocationSelectors.byIds(locationIds || [])(state)
            .map(location => location.name)
            .join(', '),
      ),
    ],
    customActions: adapter => ({
      fetchCollection: (state, payload: { scope: OfferScope }) =>
        adapter.markAsPending(state, { scope: payload.scope, entities: [] }),
      fetchActiveCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Active, entities: [] }),
      fetchArchivedCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Archived, entities: [] }),
      upsertOne: (state, payload: { data: OfferBrief; scope: OfferScope }) =>
        adapter.upsertOne(state, payload.data, { scope: payload.scope }),
      upsertMany: (state, payload: { data: OfferBrief[]; scope: OfferScope }) =>
        adapter.upsertMany(state, payload.data, { scope: payload.scope }),
      references: (state, payload: { data: OfferBrief[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

// entities
const active = createSelector(selectors.entities({ scope: OfferScope.Active }), state => state);
const archived = createSelector(selectors.entities({ scope: OfferScope.Archived }), state => state);
const all = createSelector(active, archived, (active, archived) => [...active, ...archived]);
const canAssignTo = createSelector(selectors.entities({ scope: OfferScope.Active }), state =>
  state.filter(job => CanAssignToJobsWithStatuses.includes(job.status)),
);

// meta
const activeScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Active }),
  state => state,
);
const archivedScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Archived }),
  state => state,
);

const byIds = memoizeSelector(
  (offerIds: number[]): MemoizedSelector<any, OfferBrief[]> =>
    createSelectorFactory(defaultMemoize)(
      ...(offerIds || []).map(offerId => selectors.byId(offerId)),
      (...result: OfferBrief[]) => result,
    ),
);

const customSelectors = {
  all,
  active,
  archived,
  canAssignTo,
  activeScopeIsLoaded,
  archivedScopeIsLoaded,
  byIds,
};

const jobsBriefSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  jobsBriefSelectors as JobsBriefSelectors,
  actions as JobsBriefActions,
  namespace as JobsBriefANamespace,
  reducer as JobsBriefReducer,
};
